import {
  DatePicker,
  DateTimePicker,
  renderTimeViewClock,
} from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { FC } from "react";
import { useTranslation } from "react-i18next";

import { useRtl } from "@ryde/redux";
import {
  convertUTCToISO,
  formatDateAtTimezone,
  Log,
  useFormats,
  useTimezone,
} from "@ryde/shared";

import { LocalizationProvider } from "../../localization";
import { useAllowTimeEditInMainPicker } from "./top-calendar.hook";
import {
  TopCalendarPickerStyled,
  TopCalendarTimeStyled,
} from "./top-calendar.styled";

const MIN_YEAR_OFFSET = 10;
const MAX_YEAR_OFFSET = 5;

interface ITTopCalendarPicker {
  date: TDateISO;
  setDate: (dt: TDateISO) => void;
  label?: string;
}

export const TopCalendarPicker: FC<ITTopCalendarPicker> = ({
  date,
  setDate,
  label,
}) => {
  const { dateFormat, dateTimeDelimitedFormat, formatTimeNormalized, isAMPM } =
    useFormats();
  const { direction } = useRtl();
  const { t } = useTranslation();
  const { timezone } = useTimezone();
  const allowTimeEditInMainPicker = useAllowTimeEditInMainPicker();

  const onChange = (newValueUTCDayJs: dayjs.Dayjs | null): void => {
    Log.debug(
      "Datepicker value changed to:",
      newValueUTCDayJs ? formatDateAtTimezone(newValueUTCDayJs, timezone) : "",
    );

    if (newValueUTCDayJs?.isValid()) {
      const currentYear = dayjs().year();
      const newYear = newValueUTCDayJs.year();
      const minYear = currentYear - MIN_YEAR_OFFSET;
      const maxYear = currentYear + MAX_YEAR_OFFSET;

      if (newYear < minYear || newYear > maxYear) {
        return;
      }

      setDate(convertUTCToISO(newValueUTCDayJs));
    } else {
      Log.warn(t("errors.noDateSelectedInPicker"));
    }
  };

  const timeFormatted = formatTimeNormalized(date);

  return (
    <TopCalendarPickerStyled $dir={direction}>
      <LocalizationProvider>
        {label && <label className="TopCalendarPicker-Label">{label}</label>}
        {allowTimeEditInMainPicker && (
          <DateTimePicker
            className="TopCalendarPicker-DateTimePicker"
            format={dateTimeDelimitedFormat}
            value={dayjs.utc(date)}
            timezone={timezone}
            onChange={(e: dayjs.Dayjs | null) => onChange(e)}
            ampm={isAMPM}
            viewRenderers={{
              hours: renderTimeViewClock,
              minutes: renderTimeViewClock,
              seconds: renderTimeViewClock,
            }}
            slotProps={{
              popper: {
                dir: direction,
                popperOptions: {
                  /* Important hack to prevent jumping calendar on value change
                   * it seems that modifiers set higher in component hierarchy
                   * affects lower components
                   * we set modifiers for libs/controls/src/common/top-calendar/top-header-calendar.tsx
                   *  */
                  modifiers: [],
                },
              },
            }}
          />
        )}
        {!allowTimeEditInMainPicker && (
          <DatePicker
            format={dateFormat}
            value={dayjs.utc(date)}
            timezone={timezone}
            views={["year", "month", "day"]}
            onChange={(e: dayjs.Dayjs | null) => onChange(e)}
            slotProps={{
              popper: {
                dir: direction,
                popperOptions: {
                  /* Important hack to prevent jumping calendar on value change
                   * it seems that modifiers set higher in component hierarchy
                   * affects lower components
                   * we set modifiers for libs/controls/src/common/top-calendar/top-header-calendar.tsx
                   *  */
                  modifiers: [],
                },
              },
            }}
          />
        )}
        {!allowTimeEditInMainPicker && (
          <div className="TopCalendarPicker-TimeContainer" dir="ltr">
            <span className="TopCalendarPicker-Dash">-</span>
            <TopCalendarTimeStyled className="TopCalendarPicker-Time">
              {timeFormatted}
            </TopCalendarTimeStyled>
          </div>
        )}
      </LocalizationProvider>
    </TopCalendarPickerStyled>
  );
};
